import { api } from '@api/api';
import { CaseResource } from '@api/types/case/case.resource';
import { UpsertCaseOverviewRequest } from '@api/types/case/upsert-case-overview.request';
import { makeError } from '@api/types/api-error';
import { CaseImpactResource } from '@api/types/case/case-impact/case-impact.resource';
import { CreateCaseImpactRequest } from '@api/types/case/case-impact/create-case-impact.request';
import { UpdateCaseImpactRequest } from '@api/types/case/case-impact/update-case-impact.request';
import { Option } from '@api/types/option';
import { SyncCaseGroupsRequest } from '@api/types/case/case-collaboration/sync-case-groups-request';
import { PaginatedResult } from '@api/types/paginated-result';
import { PaginatedFilters } from '@api/types/paginated-filters';
import { CaseUserResource } from '@api/types/case/case-collaboration/case-user.resource';
import { InviteCaseUserRequest } from '@api/types/case/case-collaboration/invite-case-user.request';
import { UpdateInvitedUserCaseRoleRequest } from '@api/types/case/case-collaboration/update-invited-user-case-role.request';
import { CaseItemRowResponse } from '@api/types/case/case-item-row.response';
import { CasePaginatedFilterRequest } from '@api/types/case/case-paginated-filter.request';
import { CaseStatsCounts } from '@api/types/case/case-stats-counts';
import { CompanyLocationResource } from '@api/types/company/company-location/company-location.resource';
import { CreateCompanyLocationRequest } from '@api/types/company/company-location/create-company-location.request';
import { CaseTotalsResponse } from '@api/types/case/case-totals.response';
import { CaseEvidenceRequest } from '@api/types/case/case-evidence/case-evidence.request';
import { CaseEvidenceResource } from '@api/types/case/case-evidence/case-evidence.resource';
import { CreateCaseEvidenceRequest } from '@api/types/case/case-evidence/create-case-evidence.reqest';
import { CreateCaseSolutionRequest } from '@api/types/case/case-solution/create-case-solution.request';
import { UpdateSyncGroupsRequest } from '@api/types/case/case-collaboration/update-sync-groups.request';
import { CaseOverviewTotalsResponse } from '@api/types/case/case-overview-totals.response';
import { CloseCaseRequest } from '@api/types/case/close-case.request';
import { CaseSolutionFilters } from '@api/types/case/case-solution/case-solution-filters';
import { CaseSolutionResource } from '@api/types/case/case-solution/case-solution.resource';
import { CaseSolutionItemResponse } from '@api/types/case/case-solution/case-solution-item.response';
import { UpdateCaseSolutionRequest } from '@api/types/case/case-solution/update-case-solution.request';

const caseApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCaseDetail: build.query<CaseResource, number>({
      query: (caseId) => ({
        url: `case/${caseId}`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response ? [{ id: response.caseId, type: 'Case' as const }] : [],
    }),
    deleteCase: build.mutation<void, number>({
      query: (caseId) => ({
        url: `case/${caseId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => makeError<{ caseId: number }>(error),
      invalidatesTags: (_, error, id) =>
        !error ? [{ id, type: 'Case' as const }, 'Case'] : [],
    }),
    getAllCaseData: build.query<
      PaginatedResult<CaseItemRowResponse, CaseStatsCounts>,
      CasePaginatedFilterRequest
    >({
      query: (params) => ({
        url: 'case',
        method: 'GET',
        params,
      }),
      providesTags: (response) =>
        response
          ? [
              ...response!.model.map((item) => ({
                id: item.caseId,
                type: 'Case' as const,
              })),
              'Case',
            ]
          : ['Case'],
    }),
    getCaseOverviewTotals: build.query<CaseOverviewTotalsResponse, number>({
      query: (caseId) => ({
        url: `case/${caseId}/caseOverviewTotals`,
        method: 'GET',
      }),
      providesTags: ['Case'],
    }),
    upsertCaseOverview: build.mutation<CaseResource, UpsertCaseOverviewRequest>(
      {
        query: ({ caseId, ...body }) => ({
          url: caseId != null ? `case/${caseId}` : 'case',
          method: caseId != null ? 'PUT' : 'POST',
          body,
        }),
        transformErrorResponse: (error) =>
          makeError<UpsertCaseOverviewRequest>(error),
        invalidatesTags: (_, error, { caseId }) =>
          !error
            ? caseId != null
              ? [{ id: caseId, type: 'Case' as const }]
              : ['Case']
            : [],
      }
    ),
    completeCase: build.mutation<void, number>({
      query: (caseId) => ({
        url: `case/${caseId}/complete`,
        method: 'POST',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: (_, error, id) =>
        !error ? ['Case', { id, type: 'Case' as const }] : [],
    }),
    getCaseImpacts: build.query<Array<CaseImpactResource>, number>({
      query: (caseId) => ({
        url: `case/${caseId}/caseImpact`,
        method: 'GET',
      }),
      providesTags: (response) =>
        response
          ? [
              ...response!.map((caseImpact) => ({
                id: caseImpact.caseImpactId,
                type: 'CaseImpact' as const,
              })),
              'CaseImpact',
            ]
          : ['CaseImpact'],
    }),
    createIncidentLocation: build.mutation<
      CompanyLocationResource,
      CreateCompanyLocationRequest
    >({
      query: (body) => ({
        url: 'case/incidentLocation',
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<CreateCompanyLocationRequest>(error),
    }),
    createCaseImpact: build.mutation<
      CaseImpactResource,
      CreateCaseImpactRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/caseImpact`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<CreateCaseImpactRequest>(error),
      invalidatesTags: ['CaseImpact', 'CaseTotals'],
    }),
    updateCaseImpact: build.mutation<
      CaseImpactResource,
      UpdateCaseImpactRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/caseImpact`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<UpdateCaseImpactRequest>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.caseImpactId, type: 'CaseImpact' as const },
      ],
    }),
    deleteCaseImpact: build.mutation<
      void,
      { caseId: number; caseImpactId: number }
    >({
      query: ({ caseId, caseImpactId }) => ({
        url: `case/${caseId}/caseImpact/${caseImpactId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: ['CaseImpact', 'CaseTotals'],
    }),
    getGroupOptionsForCase: build.query<Option[], number>({
      query: (caseId) => ({
        url: `case/${caseId}/companyGroup/options`,
        method: 'GET',
      }),
      providesTags: ['CaseGroup'],
    }),
    syncGroupsForCase: build.mutation<
      void,
      SyncCaseGroupsRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/companyGroup/sync`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<SyncCaseGroupsRequest>(error),
      invalidatesTags: ['CaseGroup'],
    }),
    updateSyncedGroupsForCase: build.mutation<
      void,
      UpdateSyncGroupsRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/collab`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<UpdateSyncGroupsRequest>(error),
      invalidatesTags: (_, error, { caseId }) =>
        !error ? ['Case', { id: caseId, type: 'Case' as const }] : [],
    }),
    getUsersForCase: build.query<
      PaginatedResult<CaseUserResource>,
      PaginatedFilters & { caseId: number; invitedOnly?: boolean }
    >({
      query: ({ caseId, ...params }) => ({
        url: `case/${caseId}/companyUser`,
        method: 'GET',
        params,
      }),
      providesTags: (response) =>
        response
          ? [
              ...response.model.map((user) => ({
                id: user.companyUserId,
                type: 'CaseUser' as const,
              })),
              'CaseUser',
            ]
          : ['CaseUser'],
    }),
    inviteUserToCase: build.mutation<
      CaseUserResource,
      InviteCaseUserRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/companyUser`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<InviteCaseUserRequest>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.companyUserId, type: 'CaseUser' as const },
      ],
    }),
    updateUserCaseRole: build.mutation<
      CaseUserResource,
      UpdateInvitedUserCaseRoleRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/companyUser`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<UpdateInvitedUserCaseRoleRequest>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.companyUserCaseId, type: 'CaseUser' as const },
      ],
    }),
    removeUserFromCase: build.mutation<
      void,
      { companyUserCaseId: number; caseId: number }
    >({
      query: ({ caseId, companyUserCaseId }) => ({
        url: `case/${caseId}/companyUser/${companyUserCaseId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: ['CaseUser'],
    }),
    getCaseTotals: build.query<CaseTotalsResponse, number>({
      query: (caseId) => ({
        url: `case/${caseId}/caseTotals`,
        method: 'GET',
      }),
      providesTags: ['CaseTotals'],
    }),
    getCaseTotalsForChainItem: build.query<
      CaseTotalsResponse,
      { caseId: number; chainItemId: number }
    >({
      query: ({ caseId, chainItemId }) => ({
        url: `case/${caseId}/caseTotals/${chainItemId}`,
        method: 'GET',
      }),
      providesTags: ['CaseTotals'],
    }),
    getCaseEvidenceDetail: build.query<
      CaseEvidenceResource,
      { caseId: number; evidenceId: number }
    >({
      query: ({ caseId, evidenceId }) => ({
        url: `case/${caseId}/evidence/${evidenceId}`,
        method: 'GET',
      }),
      providesTags: (_, __, { evidenceId }) => [
        'Evidence',
        { id: evidenceId, type: 'Evidence' as const },
      ],
    }),
    getCaseEvidence: build.query<
      PaginatedResult<CaseEvidenceResource>,
      CaseEvidenceRequest
    >({
      query: ({ caseId, ...params }) => ({
        url: `case/${caseId}/evidence`,
        method: 'GET',
        params,
      }),
      providesTags: ['Evidence'],
    }),
    createCaseEvidence: build.mutation<void, CreateCaseEvidenceRequest>({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/evidence`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Evidence', 'CaseTotals'],
      transformErrorResponse: (error) =>
        makeError<CreateCaseEvidenceRequest>(error),
    }),
    updateCaseEvidence: build.mutation<
      void,
      CreateCaseEvidenceRequest & { evidenceId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/evidence`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Evidence'],
      transformErrorResponse: (error) =>
        makeError<CreateCaseEvidenceRequest>(error),
    }),
    removeCaseEvidence: build.mutation<
      void,
      { evidenceId: number; caseId: number }
    >({
      query: ({ caseId, evidenceId }) => ({
        url: `case/${caseId}/evidence/${evidenceId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: ['Evidence'],
    }),
    getCaseSolutions: build.query<
      PaginatedResult<CaseSolutionItemResponse>,
      CaseSolutionFilters
    >({
      query: ({ caseId, ...params }) => ({
        url: `case/${caseId}/solution`,
        method: 'GET',
        params,
      }),
      providesTags: ['Solution'],
    }),
    getCaseSolutionById: build.query<
      CaseSolutionResource,
      { caseId: number; solutionId: number }
    >({
      query: ({ caseId, solutionId }) => ({
        url: `case/${caseId}/solution/${solutionId}`,
        method: 'GET',
      }),
    }),
    createCaseSolution: build.mutation<
      CaseSolutionItemResponse,
      CreateCaseSolutionRequest
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/solution`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<CreateCaseSolutionRequest>(error),
      invalidatesTags: ['Solution', 'CaseTotals'],
    }),
    updateCaseSolution: build.mutation<void, UpdateCaseSolutionRequest>({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/solution`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Solution'],
      transformErrorResponse: (error) =>
        makeError<UpdateCaseSolutionRequest>(error),
    }),
    removeCaseSolution: build.mutation<
      CaseSolutionItemResponse,
      { caseId: number; solutionId: number }
    >({
      query: ({ caseId, solutionId }) => ({
        url: `case/${caseId}/solution/${solutionId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) =>
        makeError<CreateCaseSolutionRequest>(error),
      invalidatesTags: ['Solution', 'CaseTotals'],
    }),
    closeCase: build.mutation<void, CloseCaseRequest>({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/closeCase`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) => makeError<CloseCaseRequest>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg.caseId, type: 'Case' as const },
        'Case',
      ],
    }),
    openCase: build.mutation<void, number>({
      query: (caseId) => ({
        url: `case/${caseId}/openCase`,
        method: 'PUT',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: (_, __, arg) => [
        { id: arg, type: 'Case' as const },
        'Case',
      ],
    }),
  }),
});

export const {
  useGetCaseOverviewTotalsQuery,
  useGetAllCaseDataQuery,
  useUpsertCaseOverviewMutation,
  useGetCaseImpactsQuery,
  useCreateCaseImpactMutation,
  useUpdateCaseImpactMutation,
  useDeleteCaseImpactMutation,
  useGetGroupOptionsForCaseQuery,
  useSyncGroupsForCaseMutation,
  useGetUsersForCaseQuery,
  useInviteUserToCaseMutation,
  useUpdateUserCaseRoleMutation,
  useRemoveUserFromCaseMutation,
  useCompleteCaseMutation,
  useGetCaseDetailQuery,
  useCreateIncidentLocationMutation,
  useGetCaseTotalsQuery,
  useGetCaseTotalsForChainItemQuery,
  useCreateCaseEvidenceMutation,
  useDeleteCaseMutation,
  useGetCaseEvidenceQuery,
  useUpdateCaseEvidenceMutation,
  useRemoveCaseEvidenceMutation,
  useGetCaseSolutionsQuery,
  useCreateCaseSolutionMutation,
  useUpdateSyncedGroupsForCaseMutation,
  useRemoveCaseSolutionMutation,
  useUpdateCaseSolutionMutation,
  useGetCaseSolutionByIdQuery,
  useCloseCaseMutation,
  useOpenCaseMutation,
  useGetCaseEvidenceDetailQuery,
  useLazyGetCaseSolutionByIdQuery,
} = caseApi;

export default caseApi;
